import { useMemo, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CalendarClock,
  CalendarPlus,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  TriangleAlert,
} from 'lucide-react'

import { attendanceApi, teachersApi } from '@/api/endpoints'
import type { AttendanceStatus, Lesson } from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { canBrowseTeachers } from '@/auth/permissions'
import { PageHeader } from '@/components/layout/AppLayout'
import { Alert, EmptyState } from '@/components/ui/Alert'
import { Button, IconButton } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { InstrumentBadge } from '@/components/ui/InstrumentBadge'
import { SkeletonRows } from '@/components/ui/Spinner'
import { useToast } from '@/components/ui/Toast'
import { cn } from '@/lib/cn'
import { ATTENDANCE_LABELS } from '@/lib/dictionaries'
import { plural } from '@/lib/format'
import { addDays, formatDayLong, formatTime, isToday, toIsoDate } from '@/lib/scheduleTime'
import { LessonFormModal } from '../schedule/LessonFormModal'
import { AttendancePicker } from './AttendancePicker'

export function AttendancePage() {
  const { user } = useAuth()
  const { notifySuccess, notifyError } = useToast()
  const queryClient = useQueryClient()

  const [anchor, setAnchor] = useState(() => new Date())
  // Pusty znaczy „wszyscy” — administracja rozlicza cały oddział, nie siebie.
  // Nauczyciel tego filtru nie ma; serwer i tak zawęża wynik do niego.
  const [teacherId, setTeacherId] = useState('')
  // Zajęcia, dla których rezerwujemy właśnie termin odrobienia.
  const [odrabiane, setOdrabiane] = useState<Lesson | null>(null)
  const dzien = toIsoDate(anchor)

  const przegladaKadre = canBrowseTeachers(user)

  const dayQuery = useQuery({
    queryKey: ['attendance', 'day', dzien, teacherId],
    queryFn: () => attendanceApi.day(dzien, teacherId || undefined),
    placeholderData: keepPreviousData,
  })

  const teachersQuery = useQuery({
    queryKey: ['teachers', 'picker'],
    queryFn: () => teachersApi.list({ size: 200 }),
    enabled: przegladaKadre,
  })

  const pendingQuery = useQuery({
    queryKey: ['attendance', 'pending'],
    queryFn: () => attendanceApi.pending({ limit: 50 }),
  })

  // Lista odrabiania idzie za filtrem prowadzącego: po wybraniu nauczyciela
  // administracja rozlicza jego dzień, więc terminy do umówienia u cudzych
  // uczniów są w tym momencie szumem. Bez filtru — komplet oddziału.
  const makeupsQuery = useQuery({
    queryKey: ['attendance', 'makeups', teacherId],
    queryFn: () =>
      attendanceApi.makeups({ only_outstanding: true, teacher_id: teacherId || undefined }),
  })

  // Po każdej zmianie odświeżamy też grafik — bloki kolorują się statusem
  // rozliczenia, więc nieodświeżony kalendarz pokazywałby nieaktualny stan.
  const odswiez = () => {
    void queryClient.invalidateQueries({ queryKey: ['attendance'] })
    void queryClient.invalidateQueries({ queryKey: ['schedule'] })
  }

  const zapisz = useMutation({
    mutationFn: ({ lessonId, status }: { lessonId: string; status: AttendanceStatus }) =>
      attendanceApi.set(lessonId, { status }),
    onSuccess: (wpis) => {
      odswiez()
      notifySuccess(`Zapisano: ${ATTENDANCE_LABELS[wpis.status]}`)
    },
    onError: (error: Error) => notifyError(error.message),
  })

  const cofnij = useMutation({
    mutationFn: (lessonId: string) => attendanceApi.clear(lessonId),
    onSuccess: () => {
      odswiez()
      notifySuccess('Wpis cofnięty')
    },
    onError: (error: Error) => notifyError(error.message),
  })

  const dane = dayQuery.data
  const zaleglosci = (pendingQuery.data ?? []).filter(
    (lekcja) => lekcja.starts_at.slice(0, 10) !== dzien,
  )
  // `?? []` tworzyłoby nową tablicę przy każdym renderze i unieważniało
  // zapamiętane grupowanie niżej.
  const doOdrobienia = useMemo(() => makeupsQuery.data ?? [], [makeupsQuery.data])

  /**
   * Zaległości w rozbiciu na prowadzących — od najstarszej.
   *
   * Administracja dostaje z serwera zaległości całego oddziału, więc samo
   * „masz zaległe wpisy” było nieprawdą: to nie są jej zajęcia i nie ona ma
   * je uzupełnić. Nazwisko przy liczbie mówi, do kogo się zwrócić, a kliknięcie
   * ustawia dzień i filtr tak, żeby te zajęcia były widoczne.
   */
  const wgProwadzacego = useMemo(() => {
    const grupy = new Map<string, { name: string; lessons: Lesson[] }>()
    for (const lekcja of zaleglosci) {
      const grupa = grupy.get(lekcja.teacher.id) ?? { name: lekcja.teacher.full_name, lessons: [] }
      grupa.lessons.push(lekcja)
      grupy.set(lekcja.teacher.id, grupa)
    }
    return [...grupy.entries()]
      .map(([id, grupa]) => ({ id, ...grupa }))
      .sort((a, b) => a.lessons[0].starts_at.localeCompare(b.lessons[0].starts_at))
  }, [zaleglosci])

  const pokazProwadzacego = przegladaKadre && teacherId === ''

  /**
   * Terminy do odrobienia w rozbiciu na uczniów.
   *
   * Uczeń z trzema nieobecnościami zajmował trzy osobne wiersze rozrzucone po
   * liście i nie było widać, że to wciąż ta sama osoba — a umawia się je jedną
   * rozmową z rodzicem. Kafelek zbiera je razem: nazwisko raz, pod nim terminy.
   *
   * Uczniowie od najstarszej zaległości, bo ta najbardziej pali; wewnątrz
   * kafelka tak samo. Serwer podaje najnowsze pierwsze, więc kolejność
   * ustawiamy tutaj.
   */
  const odrabianieWgUcznia = useMemo(() => {
    const grupy = new Map<string, { nazwa: string; terminy: Lesson[] }>()
    for (const { missed } of doOdrobienia) {
      // Zajęcia bez ucznia nie mają się z czym zgrupować — każde stoi osobno.
      const klucz = missed.student?.id ?? `bez-ucznia:${missed.id}`
      const grupa = grupy.get(klucz) ?? {
        nazwa: missed.student?.full_name ?? missed.display_title,
        terminy: [],
      }
      grupa.terminy.push(missed)
      grupy.set(klucz, grupa)
    }
    return [...grupy.entries()]
      .map(([id, grupa]) => ({
        id,
        ...grupa,
        terminy: [...grupa.terminy].sort((a, b) => a.starts_at.localeCompare(b.starts_at)),
      }))
      .sort((a, b) => a.terminy[0].starts_at.localeCompare(b.terminy[0].starts_at))
  }, [doOdrobienia])

  /** Nazwisko w nagłówku listy odrabiania — widać, czyich uczniów dotyczy. */
  const wybranyProwadzacy = (teachersQuery.data?.items ?? []).find(
    (item) => item.user.id === teacherId,
  )?.user.full_name

  const przejdzDo = (lekcja: Lesson, prowadzacy?: string) => {
    setAnchor(new Date(lekcja.starts_at))
    if (przegladaKadre) setTeacherId(prowadzacy ?? '')
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Obecności"
        description={
          user?.role === 'TEACHER'
            ? 'Zaznacz, kto był na Twoich zajęciach. Jedno kliknięcie zapisuje wpis.'
            : 'Rozliczenie zajęć w Twoich oddziałach — domyślnie wszyscy prowadzący.'
        }
      />

      {zaleglosci.length > 0 &&
        (przegladaKadre ? (
          <Alert tone="warning" title="Zaległe wpisy w oddziale">
            {plural(
              zaleglosci.length,
              'Jedne zajęcia z wcześniejszych dni czekają na rozliczenie.',
              `${zaleglosci.length} zajęcia z wcześniejszych dni czekają na rozliczenie.`,
              `${zaleglosci.length} zajęć z wcześniejszych dni czeka na rozliczenie.`,
            )}{' '}
            Wpis należy do prowadzącego — poniżej widać, u kogo zalega i od kiedy.
            <ul className="mt-2 space-y-1">
              {wgProwadzacego.map((grupa) => (
                <li key={grupa.id}>
                  <button
                    type="button"
                    className="text-left font-semibold text-primary underline underline-offset-2"
                    onClick={() => przejdzDo(grupa.lessons[0], grupa.id)}
                  >
                    {grupa.name}
                  </button>{' '}
                  —{' '}
                  {plural(
                    grupa.lessons.length,
                    'jedne zajęcia',
                    `${grupa.lessons.length} zajęcia`,
                    `${grupa.lessons.length} zajęć`,
                  )}
                  , najstarsze {formatDayLong(new Date(grupa.lessons[0].starts_at))}
                </li>
              ))}
            </ul>
          </Alert>
        ) : (
          <Alert tone="warning" title="Masz zaległe wpisy">
            {plural(
              zaleglosci.length,
              'Jedne zajęcia z wcześniejszych dni czekają na rozliczenie.',
              `${zaleglosci.length} zajęcia z wcześniejszych dni czekają na rozliczenie.`,
              `${zaleglosci.length} zajęć z wcześniejszych dni czeka na rozliczenie.`,
            )}{' '}
            <button
              type="button"
              className="font-semibold text-primary underline underline-offset-2"
              onClick={() => przejdzDo(zaleglosci[0])}
            >
              Przejdź do najstarszych
            </button>
          </Alert>
        ))}

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-outline-variant p-4">
          <div className="flex items-center gap-2">
            <IconButton
              label="Poprzedni dzień"
              icon={<ChevronLeft size={18} />}
              onClick={() => setAnchor((poprzedni) => addDays(poprzedni, -1))}
            />
            <div className="min-w-52 text-center">
              <p className="font-serif text-lg text-on-surface">{formatDayLong(anchor)}</p>
              {isToday(anchor) && <p className="text-xs text-primary">Dzisiaj</p>}
            </div>
            <IconButton
              label="Następny dzień"
              icon={<ChevronRight size={18} />}
              onClick={() => setAnchor((poprzedni) => addDays(poprzedni, 1))}
            />
            {/* {!isToday(anchor) && (
              <Button variant="ghost" size="sm" onClick={() => setAnchor(new Date())}>
                Dziś
              </Button>
            )} */}
          </div>

          {/* Ten sam filtr co w grafiku i w tej samej postaci — pasek dnia jest
              wąski, a etykieta nad polem łamałaby go na dwa wiersze. */}
          {przegladaKadre && (
            <select
              value={teacherId}
              onChange={(event) => setTeacherId(event.target.value)}
              aria-label="Prowadzący"
              className={cn(
                'cursor-pointer rounded-full border-2 border-transparent bg-surface-container-low',
                'px-4 py-2 text-sm font-semibold text-on-surface-variant transition-colors',
                'focus:border-primary focus:bg-surface focus:outline-none',
              )}
            >
              <option value="">Wszyscy nauczyciele</option>
              {(teachersQuery.data?.items ?? []).map((item) => (
                <option key={item.user.id} value={item.user.id}>
                  {item.user.full_name}
                </option>
              ))}
            </select>
          )}

          {dane && dane.summary.total > 0 && <Postep {...dane.summary} />}
        </div>

        {dayQuery.isPending ? (
          <SkeletonRows rows={3} />
        ) : dane && dane.lessons.length > 0 ? (
          <ul className="divide-y divide-outline-variant">
            {dane.lessons.map((lekcja) => (
              <li key={lekcja.id}>
                <WierszObecnosci
                  lesson={lekcja}
                  pokazProwadzacego={pokazProwadzacego}
                  isSaving={zapisz.isPending || cofnij.isPending}
                  onSelect={(status) => zapisz.mutate({ lessonId: lekcja.id, status })}
                  onClear={() => cofnij.mutate(lekcja.id)}
                />
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={<CalendarClock size={28} />}
            title="Brak zajęć tego dnia"
            description="Wybierz inny dzień lub sprawdź grafik."
          />
        )}
      </Card>

      {doOdrobienia.length > 0 && (
        <Card>
          <div className="border-b border-outline-variant p-4">
            <h2 className="font-serif text-lg text-on-surface">
              Lekcje do odrobienia
              {wybranyProwadzacy && (
                <span className="text-on-surface-variant"> — {wybranyProwadzacy}</span>
              )}
            </h2>
            <p className="text-sm text-on-surface-variant">
              Nieobecności zgłoszone co najmniej dobę wcześniej. Uczniowi należy się nowy termin —
              zarezerwuj go w grafiku.
            </p>
          </div>
          <ul className="divide-y divide-outline-variant">
            {odrabianieWgUcznia.map((uczen) => (
              <li key={uczen.id} className="p-4">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-medium text-on-surface">{uczen.nazwa}</span>
                  {/* Liczba tylko przy kilku terminach: przy jednym powtarzałaby
                      to, co widać niżej. */}
                  {uczen.terminy.length > 1 && (
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                      {plural(
                        uczen.terminy.length,
                        'termin do umówienia',
                        `${uczen.terminy.length} terminy do umówienia`,
                        `${uczen.terminy.length} terminów do umówienia`,
                      )}
                    </span>
                  )}
                </div>

                <ul className="mt-2 space-y-1.5">
                  {uczen.terminy.map((missed) => (
                    <li
                      key={missed.id}
                      className="flex flex-wrap items-center justify-between gap-3 text-sm"
                    >
                      <span className="text-on-surface-variant">
                        {formatDayLong(new Date(missed.starts_at))}, {formatTime(missed.starts_at)}
                        {pokazProwadzacego && ` · ${missed.teacher.full_name}`}
                      </span>
                      {/* Rezerwacja stąd, a nie „gdzieś w grafiku”: to jedyne miejsce,
                          w którym widać, komu i za co należy się termin. Formularz
                          przepisuje resztę danych z opuszczonej lekcji. */}
                      <Button
                        size="sm"
                        variant="outline"
                        icon={<CalendarPlus size={14} />}
                        onClick={() => setOdrabiane(missed)}
                      >
                        Zarezerwuj termin
                      </Button>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <LessonFormModal
        open={odrabiane !== null}
        onClose={() => setOdrabiane(null)}
        locationId={odrabiane?.location.id ?? ''}
        makeupFor={odrabiane}
      />
    </div>
  )
}

function Postep({ total, filled }: { total: number; filled: number }) {
  const komplet = filled >= total
  const procent = total === 0 ? 0 : Math.round((filled / total) * 100)

  return (
    <div className="flex items-center gap-3">
      <div className="h-2 w-28 overflow-hidden rounded-full bg-surface-container-low">
        <div
          className={cn(
            'h-full rounded-full transition-all',
            komplet ? 'bg-success' : 'bg-primary',
          )}
          style={{ width: `${procent}%` }}
        />
      </div>
      <p
        className={cn(
          'text-sm font-semibold',
          komplet ? 'text-success' : 'text-on-surface-variant',
        )}
        aria-live="polite"
      >
        {komplet ? 'Dzień rozliczony' : `Uzupełniono ${filled} z ${total}`}
      </p>
    </div>
  )
}

interface WierszProps {
  lesson: Lesson
  /** Widok całego oddziału — bez nazwiska nie wiadomo, czyje to zajęcia. */
  pokazProwadzacego?: boolean
  isSaving: boolean
  onSelect: (status: AttendanceStatus) => void
  onClear: () => void
}

function WierszObecnosci({
  lesson,
  pokazProwadzacego = false,
  isSaving,
  onSelect,
  onClear,
}: WierszProps) {
  const wpis = lesson.attendance
  const odwolane = lesson.status === 'CANCELLED'
  const bezUcznia = lesson.student === null
  const przyszle = new Date(lesson.starts_at) > new Date()
  const podpis = lesson.student?.full_name ?? lesson.display_title

  return (
    <div
      className={cn(
        'flex flex-col gap-3 p-4 transition sm:flex-row sm:items-center sm:justify-between',
        wpis?.is_flagged && 'bg-error-container/25',
      )}
    >
      <div className="flex min-w-0 items-start gap-3">
        <div className="shrink-0 text-right">
          <p className="font-mono text-sm font-semibold text-on-surface">
            {formatTime(lesson.starts_at)}
          </p>
          <p className="text-xs text-on-surface-variant">{lesson.duration_minutes} min</p>
        </div>
        <div className="min-w-0">
          <p className="flex items-center gap-2 truncate font-medium text-on-surface">
            {podpis}
            {lesson.is_makeup && (
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                odrabianie
              </span>
            )}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-on-surface-variant">
            {pokazProwadzacego && (
              <span className="font-semibold text-on-surface">{lesson.teacher.full_name}</span>
            )}
            <span>{lesson.room?.name ?? 'Dojazd do ucznia'}</span>
            {lesson.instrument && <InstrumentBadge instrument={lesson.instrument} />}
            {wpis?.is_flagged && (
              <span className="flex items-center gap-1 font-semibold text-error">
                <TriangleAlert size={13} />
                {ATTENDANCE_LABELS[wpis.status]}
              </span>
            )}
          </div>
        </div>
      </div>

      {odwolane ? (
        <p className="text-sm text-on-surface-variant">Zajęcia odwołane</p>
      ) : bezUcznia ? (
        <p className="text-sm text-on-surface-variant">Zajęcia bez ucznia</p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {przyszle && (
            // Bez tego brak przycisku „Obecny” wygląda jak usterka, a nie jak
            // reguła: obecność potwierdza się po zajęciach, nie przed.
            <p className="w-full text-xs text-on-surface-variant sm:w-auto sm:max-w-40 sm:text-right">
              Zajęcia jeszcze się nie odbyły — można tylko zgłosić nieobecność.
            </p>
          )}
          <AttendancePicker
            value={wpis?.status ?? null}
            onChange={onSelect}
            absenceOnly={przyszle}
            disabled={isSaving}
            label={`Obecność — ${podpis}, ${formatTime(lesson.starts_at)}`}
          />
          {wpis && (
            <IconButton
              label="Cofnij wpis"
              icon={<RotateCcw size={16} />}
              onClick={onClear}
              disabled={isSaving}
            />
          )}
        </div>
      )}
    </div>
  )
}
